/**
 * ConfirmOwnerStep — Step 4 of the Add-Building modal.
 *
 * Fields:
 *   - Legal owner entity name (text, required, <=200 chars)
 *   - Signer relationship (dropdown, 5 values per Leo's locked-in design)
 *   - Conditional: supporting document (for AUTHORIZED_REPRESENTATIVE, OTHER)
 *   - Conditional: justification textarea (for OTHER, min 50 chars)
 *
 * Mismatch policy: if the typed legal-owner string disagrees with the HPD
 * name we received from the preview endpoint, surface a non-blocking
 * inline warning (Leo's locked-in design — warn, don't block).
 */

import React, { useEffect, useId, useMemo, useRef } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/shared/ui/button";
import { Input } from "@/components/shared/ui/input";
import { Label } from "@/components/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shared/ui/select";
import { Textarea } from "@/components/shared/ui/textarea";
import {
  SIGNER_RELATIONSHIP_OPTIONS,
  type AddBuildingConfirmOwnerForm,
  type AddBuildingDraft,
  type SignerRelationship,
} from "./types";

interface ConfirmOwnerStepProps {
  draft: AddBuildingDraft;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onChangeOwnerForm: (patch: Partial<AddBuildingConfirmOwnerForm>) => void;
  onSubmit: () => void;
}

const OWNER_ENTITY_MAX = 200;
const JUSTIFICATION_MIN = 50;
const SUPPORTING_DOC_MAX_MB = 10;
const SUPPORTING_DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png";

// Loose normalization for the HPD mismatch warning. We do NOT rewrite the
// user's input — this just powers a visual warning.
function normalizeEntity(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[.,']/g, "")
    .replace(/\b(llc|inc|corp|co|ltd|lp|l\.p\.)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const ConfirmOwnerStep: React.FC<ConfirmOwnerStepProps> = ({
  draft,
  submitting,
  submitError,
  onBack,
  onChangeOwnerForm,
  onSubmit,
}) => {
  const ownerId = useId();
  const relationshipId = useId();
  const justificationId = useId();
  const docId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const form = draft.confirmOwner;
  const hpdOwner = draft.selectedMatch?.identity.hpdOwnerName ?? null;
  const option = SIGNER_RELATIONSHIP_OPTIONS.find(
    (o) => o.value === form.signerRelationship,
  );

  const mismatchWarning = useMemo(() => {
    if (!hpdOwner) return null;
    if (!form.legalOwnerEntity.trim()) return null;
    const a = normalizeEntity(form.legalOwnerEntity);
    const b = normalizeEntity(hpdOwner);
    if (!a || !b) return null;
    if (a === b) return null;
    return `HPD lists the owner as "${hpdOwner}". You can still proceed — Carbon Clear staff may route this to review during attestation if the cross-reference still doesn't match.`;
  }, [hpdOwner, form.legalOwnerEntity]);

  const ownerTrimmed = form.legalOwnerEntity.trim();
  const justificationTrimmed = form.justification.trim();
  const needsJustification = option?.requiresJustification ?? false;
  const needsDoc = option?.requiresSupportingDoc ?? false;
  const docTooBig = form.supportingDoc
    ? form.supportingDoc.size > SUPPORTING_DOC_MAX_MB * 1024 * 1024
    : false;

  const canSubmit =
    ownerTrimmed.length > 0 &&
    ownerTrimmed.length <= OWNER_ENTITY_MAX &&
    form.signerRelationship !== null &&
    (!needsJustification || justificationTrimmed.length >= JUSTIFICATION_MIN) &&
    (!needsDoc || (form.supportingDoc !== null && !docTooBig));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onChangeOwnerForm({ supportingDoc: file });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-tight focus:outline-none"
        >
          Confirm the owner
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          NYC requires the owner-of-record as listed on the Department of
          Finance (DOF) — or Housing Preservation (HPD) for condos and co-ops.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={ownerId}>
          Legal owner name
          <span className="text-danger ml-1" aria-hidden="true">
            *
          </span>
        </Label>
        <Input
          id={ownerId}
          autoComplete="organization"
          required
          maxLength={OWNER_ENTITY_MAX}
          placeholder="e.g. ACME HOLDINGS LLC"
          value={form.legalOwnerEntity}
          onChange={(e) =>
            onChangeOwnerForm({ legalOwnerEntity: e.target.value })
          }
          aria-describedby={mismatchWarning ? `${ownerId}-warn` : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Enter the entity name exactly as it appears on the deed or HPD
          registration. Don&apos;t enter your personal name unless you own the
          building personally.
        </p>
        {mismatchWarning && (
          <div
            id={`${ownerId}-warn`}
            role="status"
            aria-live="polite"
            className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm flex gap-2"
          >
            <AlertTriangle
              className="h-4 w-4 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span>{mismatchWarning}</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={relationshipId}>
          Your relationship to the owner
          <span className="text-danger ml-1" aria-hidden="true">
            *
          </span>
        </Label>
        <Select
          value={form.signerRelationship ?? undefined}
          onValueChange={(v: string) =>
            onChangeOwnerForm({
              signerRelationship: v as SignerRelationship,
              // Reset conditional fields when the relationship changes.
              justification: "",
              supportingDoc: null,
            })
          }
        >
          <SelectTrigger id={relationshipId}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {SIGNER_RELATIONSHIP_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {needsJustification && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={justificationId}>
            Justification
            <span className="text-danger ml-1" aria-hidden="true">
              *
            </span>
          </Label>
          <Textarea
            id={justificationId}
            required
            minLength={JUSTIFICATION_MIN}
            rows={3}
            placeholder={`Describe your relationship to the owner and why you're authorized to sign (at least ${JUSTIFICATION_MIN} characters).`}
            value={form.justification}
            onChange={(e) =>
              onChangeOwnerForm({ justification: e.target.value })
            }
            aria-describedby={`${justificationId}-help`}
          />
          <p
            id={`${justificationId}-help`}
            className="text-xs text-muted-foreground"
          >
            {justificationTrimmed.length} / {JUSTIFICATION_MIN} minimum
            characters.
          </p>
        </div>
      )}

      {needsDoc && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={docId}>
            Supporting document
            <span className="text-danger ml-1" aria-hidden="true">
              *
            </span>
          </Label>
          <input
            id={docId}
            type="file"
            accept={SUPPORTING_DOC_ACCEPT}
            onChange={handleFileChange}
            className="block text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
          />
          <p className="text-xs text-muted-foreground">
            PDF, JPG, or PNG. Max {SUPPORTING_DOC_MAX_MB} MB.
            {form.supportingDoc && ` Selected: ${form.supportingDoc.name}`}
          </p>
          {docTooBig && (
            <p role="alert" className="text-sm text-danger">
              That file is larger than {SUPPORTING_DOC_MAX_MB} MB. Try a smaller
              copy.
            </p>
          )}
        </div>
      )}

      {submitError && (
        <div
          role="alert"
          className="rounded-md border border-danger/30 bg-danger/10 p-3 text-sm"
        >
          {submitError}
        </div>
      )}

      <div className="flex flex-row justify-between gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting && (
            <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
          )}
          Add building
        </Button>
      </div>
    </div>
  );
};

export default ConfirmOwnerStep;
